import { useState, useEffect, useRef } from 'react';

import {
  CalendarDays,
  CheckCircle2,
  Copy,
  Check,
  Lightbulb,
  Mail,
  Save,
  Sparkles,
  Target,
  Video,
  Download,
  Image as ImageIcon,
  Type,
  ClipboardList,
  BarChart3,
  RefreshCw,
  Printer,
  ClipboardCopy,
  Link2,
  MessageSquareMore,
  Share2,
  ThumbsUp,
  Timer,
  Square,
} from 'lucide-react';
import ScoreMeter from './ScoreMeter';
import VideoPlayer from './VideoPlayer';
import {
  fetchAssetForDownload,
  getAssetUrl,
  regenerateField,
  regenerateVideo,
  retryCopyReview,
  uploadCampaignReference,
  submitFeedback,
  createReviewInvitation, listReviewInvitations, revokeReviewInvitation,
} from '../services/api';
import { toast } from './Toaster';
import { formatGenerationDurationMs, getGenerationDurationMs } from '../utils/generationTiming';

export function formatStoryboardScene(scene, index = 0, fps = 24) {
  if (typeof scene === 'string') return scene;
  if (!scene || typeof scene !== 'object') return '';
  const rows = [`Scene ${index + 1}: ${scene.start || ''}–${scene.end || ''}`, scene.shot_intent || scene.prompt || ''];
  const fields = [['scene_description', 'Scene'], ['setting', 'Setting'], ['lens', 'Lens'], ['camera', 'Camera'],
    ['composition', 'Composition'], ['lighting', 'Lighting'], ['depth_of_field', 'Focus'],
    ['subject_motion', 'Subject action'], ['environment_motion', 'Environment motion'],
    ['mood', 'Mood'], ['transition', 'Transition'], ['opening_frame', 'Opening frame'], ['closing_frame', 'Closing frame'],
    ['negative_prompt', 'Excluded elements']];
  fields.forEach(([key, label]) => { if (scene[key]) rows.push(`${label}: ${scene[key]}`); });
  (scene.objects || []).forEach(object => rows.push(
    `Object ${object.object_id}: exactly ${object.count} ${object.description}; ${object.position}; ${object.action}.`));
  (scene.spatial_relationships || []).forEach(value => rows.push(`Spatial relationship: ${value}`));
  (scene.requirements || []).forEach(value => rows.push(`Required: ${value}`));
  const seconds = value => String(value || '0').split(':').reverse().reduce((total, part, i) => total + Number(part) * (60 ** i), 0);
  const count = Math.max(1, Math.round((seconds(scene.end) - seconds(scene.start)) * fps));
  (scene.frame_beats || []).forEach(beat => rows.push(
    `Scene frames ${Math.round(beat.start * count) + 1}–${Math.round(beat.end * count)}: ${beat.description}`));
  if (scene.generation_prompt) rows.push(`Video generation prompt: ${scene.generation_prompt}`);
  return rows.filter(Boolean).join('\n');
}

export const sanitizeDownloadSegment = (value) => String(value || '')
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/&/g, ' and ')
  .replace(/[^a-z0-9]+/gi, '-')
  .replace(/^-+|-+$/g, '')
  .toLowerCase()
  .slice(0, 36);

export const buildAssetDownloadFilename = (
  campaign,
  assetLabel,
  sequence,
  extension,
) => {
  const input = campaign?.input || {};
  const base = sanitizeDownloadSegment(
    input.brand_name
    || input.company_name
    || campaign?.brand_name
    || 'ad',
  ) || 'ad';
  const suffix = String(Math.max(1, Number(sequence) || 1)).padStart(2, '0');
  const ext = sanitizeDownloadSegment(extension) || 'bin';
  return `${base}-${suffix}.${ext}`;
};

const handleDownload = async (reference, filename, resolver) => {
  try {
    const blob = await fetchAssetForDownload(reference, resolver);
    const link = document.createElement('a');
    const objectUrl = URL.createObjectURL(blob);
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Some browsers finish consuming the Blob after the synthetic click.
    // Delayed revocation prevents a truncated/empty file.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
  } catch (error) {
    console.error('Download failed', error);
    toast(
      error?.message || 'The generated file could not be downloaded. Please try again.',
      'error',
    );
  }
};

const TABS = [
  { id: 'creatives', label: 'Creatives', icon: ImageIcon },
  { id: 'copy', label: 'Copy', icon: Type },
  { id: 'strategy', label: 'Strategy', icon: BarChart3 },
  { id: 'planning', label: 'Planning', icon: ClipboardList },
];

export function identityLabel(report) {
  const vision = report.vision_review || report.qa_report?.vision_review || {};
  const reviews = Object.keys(vision).some(key => key.startsWith('variant_')) ? Object.values(vision) : [vision];
  const rejected = reviews.some(review => (review.findings || []).some(item => String(item.code || '').includes('identity') && ['high', 'critical'].includes(item.severity)));
  if (rejected || report.product_identity_review === 'failed') return 'Reference used · identity review failed';
  if (report.product_identity_review === 'passed' && reviews.every(review => review.evaluated && review.passed) && !report.review_required) return 'Identity review passed';
  return 'Reference used · identity not verified';
}

function ResultDashboard({
  campaign,
  onSave,
  saved,
  onUpdateCampaign,
  reviewMode = false,
  onReviewDecision = null,
  existingReview = null,
  assetResolver = undefined,
  generationInProgress = false,
  generationJob = null,
  onStopGeneration = null,
  stoppingGeneration = false,
  canStopGeneration = false,
}) {
  const [copiedField, setCopiedField] = useState(null);
  const [activeTab, setActiveTab] = useState('creatives');
  const [activeResearchTab, setActiveResearchTab] = useState('approach');

  const [regenField, setRegenField] = useState('');
  const [copiedAll, setCopiedAll] = useState(false);
  const [reviewStatus, setReviewStatus] = useState('');
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewSaved, setReviewSaved] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [invitations, setInvitations] = useState([]);
  const [invitationUrl, setInvitationUrl] = useState('');
  const [sharing, setSharing] = useState(false);
  const [videoRetryBusy, setVideoRetryBusy] = useState(false);
  const [videoRetryStatus, setVideoRetryStatus] = useState('');
  const [imageRetryBusy, setImageRetryBusy] = useState(false);
  const [imageRetryStatus, setImageRetryStatus] = useState('');
  const [artworkUploadBusy, setArtworkUploadBusy] = useState(false);
  const [copyRetryBusy, setCopyRetryBusy] = useState(false);
  const [copyRetryStatus, setCopyRetryStatus] = useState('');
  const currentCampaign = useRef(campaign);
  currentCampaign.current = campaign;
  const campaignId = campaign?.id;

  useEffect(() => {
    setReviewStatus(existingReview ? `Decision already recorded: ${existingReview.accepted ? 'approved' : 'changes requested'}.` : '');
    setReviewSaved(Boolean(existingReview));
    setReviewNotes(''); setInvitationUrl(''); setInvitations([]);
  }, [campaign?.id, existingReview]);

  useEffect(() => {
    if (campaignId) {
      setActiveTab('creatives');
      setImageRetryStatus('');
    }
  }, [campaignId]);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`);
  };

  const handleVideoRetry = async () => {
    const id = campaign.id;
    setVideoRetryBusy(true);
    setVideoRetryStatus('Queuing video retry…');
    try {
      const result = await regenerateVideo(campaign, (job, data) => {
        setVideoRetryStatus(job?.message || 'Generating and checking video…');
        if (currentCampaign.current?.id === id && data?.result?.creative_assets?.video_recovery) {
          onUpdateCampaign?.(data.result);
        }
      });
      if (currentCampaign.current?.id === id && result) onUpdateCampaign?.(result);
      toast('Video retry completed', 'success');
    } catch (error) {
      toast(error.message || 'Video retry failed', 'error');
    } finally {
      setVideoRetryBusy(false);
      setVideoRetryStatus('');
    }
  };

  const handleCopyReviewRetry = async () => {
    const id = campaign.id;
    setCopyRetryBusy(true);
    setCopyRetryStatus('Queuing review of the saved copy…');
    try {
      const result = await retryCopyReview(campaign, (job) => setCopyRetryStatus(job?.message || 'Reviewing copy and generating media…'));
      if (currentCampaign.current?.id === id && result) onUpdateCampaign?.(result);
      toast('Copy review and media generation completed', 'success');
    } catch (error) {
      toast(error.message || 'Copy review could not finish. Your draft is retained.', 'error');
    } finally {
      setCopyRetryBusy(false);
      setCopyRetryStatus('');
    }
  };

  const handleImageRetry = async (candidate = campaign) => {
    const id = candidate.id;
    setImageRetryBusy(true);
    try {
      const result = await retryCopyReview(candidate, (job, data) => {
        setImageRetryStatus(job?.message || 'Resuming image generation…');
        if (currentCampaign.current?.id === id && data?.result?.creative_assets) onUpdateCampaign?.(data.result);
      });
      if (currentCampaign.current?.id === id && result) onUpdateCampaign?.(result);
    } catch (error) {
      toast(error.message || 'Image recovery could not finish. Approved images are retained.', 'error');
    } finally {
      setImageRetryBusy(false);
      setImageRetryStatus('');
    }
  };

  const uploadRecoveryArtwork = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const original = campaign;
    setArtworkUploadBusy(true);
    try {
      const uploaded = await uploadCampaignReference(file, 'exact_artwork');
      const reference = uploaded.url_relative || uploaded.url;
      if (!reference) throw new Error('The artwork upload did not return a usable reference.');
      if (currentCampaign.current !== original) return;
      const updated = { ...original, input: { ...original.input,
        product_image_mode: 'exact_artwork', product_image_urls: reference } };
      onUpdateCampaign?.(updated);
      setImageRetryStatus('Transparent product artwork uploaded. Resume image generation using the saved copy.');
    } catch (error) {
      toast(error.message || 'Could not upload the product artwork.', 'error');
    } finally {
      setArtworkUploadBusy(false);
      event.target.value = '';
    }
  };

  const generateRecoveryFromDescription = () => {
    const updated = { ...campaign, input: { ...campaign.input,
      product_image_mode: 'reference', product_image_urls: '' } };
    onUpdateCampaign?.(updated);
    return handleImageRetry(updated);
  };

  const copyToClipboard = (text, fieldId) => {
    navigator.clipboard?.writeText(text || '');
    setCopiedField(fieldId);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const renderCopyButton = (text, fieldId) => {
    const isCopied = copiedField === fieldId;
    return (
      <button className="cs-copy-btn" onClick={() => copyToClipboard(text, fieldId)} title="Copy">
        {isCopied ? <Check size={15} style={{ color: '#10b981' }} /> : <Copy size={15} color="var(--muted)" />}
      </button>
    );
  };

  const handleRegenField = async (field) => {
    if (regenField) return;
    setRegenField(field);
    try {
      const res = await regenerateField(campaign, field);
      if (currentCampaign.current !== campaign) return;
      const changed = res.status === 'changed' || (!res.status && res.value !== campaign[field] && !res.note);
      if (!changed) {
        toast(res.note || 'Original copy retained.', res.status === 'failed' ? 'error' : 'info');
        return;
      }
      if (onUpdateCampaign) onUpdateCampaign({ ...campaign, [field]: res.value,
        claim_verification: res.claim_verification || null,
        evaluation_stale: true,
        creative_copy_stale: Boolean(campaign.creative_copy_stale || ['ad_headline', 'ad_subheadline', 'ad_body', 'call_to_action'].includes(field)),
      });
      toast('Copy updated. Save your changes.', 'success');
    } catch (err) {
      toast('Could not regenerate this field', 'error');
    } finally {
      setRegenField('');
    }
  };

  const renderRegenButton = (field) => reviewMode ? null : (
    <button className="cs-copy-btn cs-regen-field" onClick={() => handleRegenField(field)} disabled={!!regenField} title="Regenerate with AI">
      <RefreshCw size={14} className={regenField === field ? 'spin-icon' : ''} color="var(--muted)" />
    </button>
  );

  const copyAllText = () => {
    if (!campaign) return;
    const blocks = [
      `# ${campaign.campaign_title}`,
      campaign.positioning_statement,
      `\n## Ad copy\n${campaign.ad_headline}\n${campaign.ad_subheadline}\n${campaign.ad_body}\nCTA: ${campaign.call_to_action}`,
      `\n## Social\n${campaign.social_hook}\n${campaign.social_caption}\n${(campaign.hashtags || []).join(' ')}`,
      `\n## Email\nSubject: ${campaign.sales_email_subject}\n${campaign.sales_email_body}`,
    ];
    navigator.clipboard?.writeText(blocks.filter(Boolean).join('\n'));
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
    toast('All campaign text copied', 'success');
  };

  const copyReviewLink = async () => {
    if (!campaign || sharing) return;
    if (!saved) { toast('Save the campaign before sharing it', 'info'); return; }
    setSharing(true);
    try {
      const invitation = await createReviewInvitation(campaign.id);
      const reviewUrl = new URL(window.location.origin + window.location.pathname);
      reviewUrl.hash = new URLSearchParams({ review: invitation.token }).toString();
      setInvitationUrl(reviewUrl.toString());
      setInvitations(current => [invitation, ...current]);
      try { await navigator.clipboard.writeText(reviewUrl.toString()); toast('Private review invitation copied. Expires in 7 days.', 'success'); }
      catch { toast('Invitation created. Copy the link shown below.', 'info'); }
    } catch { toast('Could not create the review invitation', 'error'); }
    finally { setSharing(false); }
  };

  const manageInvitations = async () => {
    try { const result = await listReviewInvitations(campaign.id); setInvitations(result.invitations || []); }
    catch { toast('Could not load review invitations', 'error'); }
  };
  const revokeInvitation = async (id) => {
    try {
      await revokeReviewInvitation(id);
      setInvitations(current => current.map(item => item.id === id ? { ...item, revoked_at: new Date().toISOString() } : item));
      setInvitationUrl('');
    } catch { toast('Could not revoke this invitation', 'error'); }
  };

  const submitReviewDecision = async (approved) => {
    if (approved && (campaign.release_qa?.blocked || campaign.creative_assets?.image_recovery
        || campaign.creative_assets?.quality_report?.passed === false)) {
      toast('Resolve the failed media checks before approving this draft.', 'error');
      return;
    }
    if (!campaign || reviewBusy || reviewSaved) return;
    setReviewBusy(true);
    setReviewStatus('Saving your review…');
    try {
      if (onReviewDecision) await onReviewDecision(approved, reviewNotes);
      else await submitFeedback({
        campaign_id: campaign.id,
        user_rating: approved ? 5 : 3,
        accepted: approved,
        defect_codes: approved ? [] : ['reviewer_requested_changes'],
        feedback_text: approved ? 'Approved via shared campaign review.' : 'Changes requested via shared campaign review.',
        prompt_strategy: campaign.prompt_strategy || '',
      });
      setReviewSaved(true);
      setReviewStatus(approved ? 'Campaign approved. Thank you.' : 'Change request recorded. Thank you.');
      toast(approved ? 'Campaign approved' : 'Change request recorded', 'success');
    } catch (error) {
      setReviewStatus('Could not save the review. Please try again.');
      toast('Could not save the review', 'error');
    } finally {
      setReviewBusy(false);
    }
  };

  const exportPrint = () => {
    if (!campaign) return;
    const esc = (s) => String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
    const html = `<!doctype html><html><head><title>${esc(campaign.campaign_title)}</title>
      <style>body{font-family:Arial,sans-serif;max-width:760px;margin:40px auto;color:#111;line-height:1.6}
      h1{font-size:24px} h2{font-size:16px;margin-top:24px;border-bottom:1px solid #ddd;padding-bottom:4px}
      .muted{color:#555} .tag{color:#2563eb}</style></head><body>
      <h1>${esc(campaign.campaign_title)}</h1>
      <p class="muted">${esc(campaign.positioning_statement)}</p>
      <h2>Advertising copy</h2>
      <p><strong>${esc(campaign.ad_headline)}</strong><br>${esc(campaign.ad_subheadline)}<br>${esc(campaign.ad_body)}<br><em>CTA: ${esc(campaign.call_to_action)}</em></p>
      <h2>Social content</h2>
      <p><strong>${esc(campaign.social_hook)}</strong></p>
      <p>${esc(campaign.social_caption).replace(/\n/g, '<br>')}</p>
      <p class="tag">${esc((campaign.hashtags || []).join(' '))}</p>
      <h2>Sales email</h2>
      <p><strong>${esc(campaign.sales_email_subject)}</strong></p>
      <p>${esc(campaign.sales_email_body).replace(/\n/g, '<br>')}</p>
      </body></html>`;
    const win = window.open('', '_blank');
    if (!win) {
      toast('Allow pop-ups to export', 'error');
      return;
    }
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 350);
  };

  if (!campaign) {
    return (
      <section className="panel cs-empty glow-card" onMouseMove={handleMouseMove}>
        <div className="cs-empty-icon"><Sparkles size={30} /></div>
        <h2>Your campaign studio is ready</h2>
        <p>Fill in the brief and generate to see ad creatives, copy, strategy scores and a content plan here.</p>
      </section>
    );
  }

  const evaluation = campaign.evaluation || {};
  const research = campaign.research_notes || {};
  const assets = campaign.creative_assets || {};
  const videoAvailable = Boolean(assets.video_url);
  const videoRequested = campaign.input?.generate_video === true
    || (campaign.input?.generate_video == null && videoAvailable);
  // A completed media asset is authoritative. Never hide a real video because
  // a partial job update carried a missing or stale generate_video flag.
  const videoEnabled = videoRequested || videoAvailable;
  const quality = assets.quality_report || campaign.creative_quality || {};
  const generatedImageVariants = Array.isArray(assets.image_variants)
    ? assets.image_variants.filter((variant) => variant?.image_url)
    : [];
  const imageVariants = generatedImageVariants.length > 0
    ? generatedImageVariants
    : [
        { id: 'portrait-output', label: 'Portrait creative', format: 'portrait', image_url: assets.portrait_post_url || assets.square_ad_url },
        { id: 'full-screen-output', label: 'Full-screen creative', format: 'full_screen', image_url: assets.full_screen_ad_url },
      ].filter((variant) => variant.image_url);
  const groundingSources = campaign.grounding_sources || [];
  const overall = evaluation.overall || 0;
  const scoresAvailable = evaluation.status !== 'not_evaluated';
  const generationDurationMs = getGenerationDurationMs(campaign);
  const brandColors = String(campaign.input?.brand_colors || '')
    .split(',')
    .map((color) => color.trim())
    .filter((color) => /^#[0-9a-f]{3,8}$/i.test(color))
    .slice(0, 5);

  const researchTabs = [
    { id: 'approach', label: 'Approach', content: research.generation_approach },
    { id: 'persuasion', label: 'Persuasion', content: research.persuasion_framework },
    { id: 'rationale', label: 'Rationale', content: research.why_this_should_work },
    { id: 'testing', label: 'Testing', content: research.testing_recommendation },
    { id: 'risks', label: 'Risks', content: research.risks_and_limitations },
  ];
  const activeNote = researchTabs.find((tab) => tab.id === activeResearchTab);

  return (
    <section className="cs-output">
      {campaign.evaluation_stale && <p role="status" className="note-box">Copy has changed. The previous AI scores have not been updated.</p>}
      {campaign.creative_copy_stale && <p className="note-box">Existing images and video still contain the earlier copy. Regenerate or edit those assets before publishing.</p>}
      {!reviewMode && campaign.claim_verification && <div className="note-box">
        <strong>Claim review: {campaign.claim_verification.status || 'Legacy lexical check'} · semantic review {campaign.claim_verification.semantic_status || 'not checked'}</strong>
        <p>{campaign.claim_verification.summary}</p>
        {campaign.claim_verification.semantic_status === 'unavailable'
          && !campaign.creative_assets?.portrait_post_url && !campaign.creative_assets?.full_screen_ad_url && (
          <div>
            <p>Text draft saved. Review must finish before images and video can be generated.</p>
            <button type="button" className="btn btn-secondary" onClick={handleCopyReviewRetry}
              disabled={copyRetryBusy || generationInProgress}>
              {copyRetryBusy ? 'Reviewing saved copy…' : 'Retry copy review'}
            </button>
            {copyRetryStatus && <p role="status">{copyRetryStatus}</p>}
          </div>
        )}
        {(campaign.claim_verification.unsupported_claims || []).map(claim => <p key={claim}>Needs review: {claim}</p>)}
      </div>}
      {reviewMode && (
        <div className="client-review-banner reveal-up">
          <div className="client-review-copy">
            <span><MessageSquareMore size={17} /></span>
            <div>
              <small>SHARED CAMPAIGN REVIEW</small>
              <strong>Review the complete direction and record your decision.</strong>
              {reviewStatus && <p role="status">{reviewStatus}</p>}
              {onReviewDecision && !reviewSaved && <label>Review notes<textarea value={reviewNotes} onChange={event => setReviewNotes(event.target.value)} maxLength={1000} disabled={reviewBusy} /></label>}
            </div>
          </div>
          <div className="client-review-actions">
            <button type="button" onClick={() => submitReviewDecision(false)} disabled={reviewBusy || reviewSaved}>
              <MessageSquareMore size={15} /> Request changes
            </button>
            <button type="button" className="approve" onClick={() => submitReviewDecision(true)} disabled={reviewBusy || reviewSaved || campaign.release_qa?.blocked || Boolean(assets.image_recovery) || assets.quality_report?.passed === false}>
              <ThumbsUp size={15} /> Approve campaign
            </button>
          </div>
        </div>
      )}

      {/* SUMMARY BAR */}
      <div className="panel cs-summary glow-card reveal-up" onMouseMove={handleMouseMove}>
        <div className="cs-summary-info">
          <p className="eyebrow">Generated Campaign</p>
          <div className="cs-summary-title">
            <h2>{campaign.campaign_title}</h2>
            {!reviewMode && <span className="strategy-badge cs-badge">{campaign.prompt_strategy || 'Balanced'}</span>}
            {generationDurationMs != null && (
              <span className="generation-duration-badge">
                <Timer size={13} aria-hidden="true" />
                Generated in {formatGenerationDurationMs(generationDurationMs)}
              </span>
            )}
          </div>
          <p className="cs-summary-positioning">{campaign.positioning_statement}</p>
          <div className="brand-dna-rail">
            <div className="brand-dna-mark">{String(campaign.input?.brand_name || campaign.campaign_title || 'A').charAt(0).toUpperCase()}</div>
            <div className="brand-dna-copy">
              <small>CAMPAIGN DNA</small>
              <strong>{campaign.input?.brand_name || 'Manual brand'} · {campaign.input?.product_name || 'Campaign'}</strong>
            </div>
            <div className="brand-dna-signals">
              {brandColors.length > 0 && <span className="brand-dna-colors">{brandColors.map((color) => <i key={color} style={{ backgroundColor: color }} title={color} />)}</span>}
              <span><b>Voice</b>{campaign.input?.tone || 'Campaign tone'}</span>
              <span><b>Source</b>{groundingSources.length > 0 ? `${groundingSources.length} grounded references` : 'Focused brief'}</span>
            </div>
          </div>
          {campaign.input && (
            <div className="cs-chips">
              <span className="cs-chip"><b>Goal</b>{campaign.input.campaign_goal}</span>
              <span className="cs-chip"><b>Tone</b>{campaign.input.tone}</span>
              <span className="cs-chip"><b>Platform</b>{campaign.input.platform}</span>
              <span className="cs-chip"><b>Outputs</b>Portrait + full-screen{videoEnabled ? ' + video' : ''}</span>
              {quality.brand_logo_integrated && (
                <span className="cs-chip">
                  <b>Brand</b>Logo verified {Math.round(Number(quality.logo_integration_score || 0) * 100)}%
                </span>
              )}
              {quality.company_wordmark_integrated && (
                <span className="cs-chip">
                  <b>Brand</b>Company wordmark verified
                </span>
              )}
              {quality.product_reference_integrated && (
                <span className="cs-chip">
                  <b>Product</b>{identityLabel(quality)}
                </span>
              )}
              {Number.isFinite(Number(quality.score ?? quality.perceptual_score)) && (
                <span className="cs-chip">
                  <b>Media QA</b>{Number(quality.score ?? quality.perceptual_score)}/100
                  {quality.passed === false ? ' · failed' : ''}
                  {quality.review_required ? ' · review required' : ''}
                </span>
              )}
              {assets.video_url && assets.video_quality?.delivery_state && (
                <span className="cs-chip">
                  <b>Video QA</b>{assets.video_quality.score}/100 · {assets.video_quality.delivery_state}
                  {assets.video_quality.review_required ? ' · review required' : ''}
                </span>
              )}
            </div>
          )}
          {campaign.note && <div className="note-box cs-note">{campaign.note}</div>}
          {assets.video_generation_mode === 'wan_background_composited_software' && (
            <div className="note-box cs-note">Illustrative software workflow with AI-generated background motion. This is a concept presentation, not a recording of the product’s actual interface.</div>
          )}
        </div>

        {!reviewMode && <div className="cs-summary-side">
          <div className="cs-ring" style={{ '--ring': `${overall * 3.6}deg` }}>
            <div className="cs-ring-inner">
              <span>{scoresAvailable ? overall : '—'}</span>
              <small>{scoresAvailable ? 'AI estimate' : 'Not evaluated'}</small>
            </div>
          </div>
          <button className="primary-button cs-save" onClick={onSave} disabled={saved}>
            {saved ? <CheckCircle2 size={17} /> : <Save size={17} />}
            {saved ? 'Saved' : 'Save campaign'}
          </button>
        </div>}
      </div>

      {/* TOOLBAR */}
      <div className="cs-toolbar">
        {!reviewMode && <button className="cs-tool-btn" onClick={copyReviewLink} disabled={!saved || sharing} title="Create a private 7-day review invitation">
          <Share2 size={15} /> Share review link
        </button>}
        {!reviewMode && saved && <button className="cs-tool-btn" onClick={manageInvitations}>Manage review invitations</button>}
        <button className="cs-tool-btn" onClick={copyAllText}>
          {copiedAll ? <Check size={15} /> : <ClipboardCopy size={15} />} Copy all text
        </button>
        <button className="cs-tool-btn" onClick={exportPrint}>
          <Printer size={15} /> Export / Print
        </button>
        {reviewMode && <span className="cs-review-link-status"><Link2 size={13} /> Presentation mode</span>}
      </div>

      {!reviewMode && (invitationUrl || invitations.length > 0) && <div className="panel note-box">
        <p>Anyone with an invitation link can view that saved version and submit one decision. Links expire after 7 days and can be revoked here. Previously downloaded media cannot be recalled.</p>
        {invitationUrl && <label>Private invitation link<input readOnly value={invitationUrl} onFocus={event => event.target.select()} /></label>}
        {invitations.map(item => <div key={item.id}>
          <span>Created {new Date(item.created_at).toLocaleString()} · {item.revoked_at ? 'Revoked' : `Expires ${new Date(item.expires_at).toLocaleString()}`}</span>
          {item.review && <p>{item.review.accepted ? 'Approved' : 'Changes requested'}: {item.review.notes}</p>}
          {!item.revoked_at && <button onClick={() => revokeInvitation(item.id)}>Revoke invitation</button>}
        </div>)}
      </div>}

      {/* TAB NAVIGATION */}
      <div className="cs-tabs">
        {TABS.filter(tab => !reviewMode || ['creatives', 'copy'].includes(tab.id)).map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              className={`cs-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={16} /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* ===== CREATIVES TAB ===== */}
      {activeTab === 'creatives' && (
        <div className="cs-panel reveal-up">
          <div className="cs-grid">
            {videoAvailable && (
              <article className="panel output-card glow-card cs-media-card cs-video-card" onMouseMove={handleMouseMove}>
                <div className="card-title-row"><Video size={18} /><h3>Video Ad</h3></div>
                <div className="cs-media-stage">
                  <VideoPlayer
                    src={getAssetUrl(assets.video_url)}
                    poster={imageVariants[0]?.image_url ? getAssetUrl(imageVariants[0].image_url) : undefined}
                    scenes={campaign.video_generation_prompts || []}
                  />
                </div>
                <button
                  onClick={() => handleDownload(
                    assets.video_url_relative || assets.video_url,
                    buildAssetDownloadFilename(campaign, 'video-ad', 1, 'mp4'),
                    assetResolver,
                  )}
                  className="primary-button cs-dl"
                >
                  <Download size={14} /> Download MP4
                </button>
              </article>
            )}
            {videoRequested && !videoAvailable && generationInProgress && (
              <article className="panel output-card glow-card cs-media-card cs-video-card cs-video-pending" role="status" aria-live="polite">
                <div className="card-title-row"><Video size={18} /><h3>Video Ad</h3></div>
                <div className="cs-video-pending-body">
                  <RefreshCw size={28} className="spin-icon" />
                  <strong>Video generation continues in the background</strong>
                  <p>Your completed images are ready now. The video will appear here automatically when encoding and verification finish.</p>
                  <small>{stoppingGeneration
                    ? 'Stopping generation and releasing the GPU…'
                    : (generationJob?.message || 'Preparing cinematic video…')}</small>
                  {onStopGeneration && (
                    <button
                      type="button"
                      className="generation-stop-button"
                      onClick={onStopGeneration}
                      disabled={stoppingGeneration || !canStopGeneration}
                      title="Stop the video generation and release the GPU. The images above are kept."
                    >
                      <Square size={14} aria-hidden="true" />
                      {stoppingGeneration ? 'Stopping…' : 'Stop video generation'}
                    </button>
                  )}
                </div>
              </article>
            )}
          </div>

          {assets.image_recovery && !generationInProgress && !reviewMode && onUpdateCampaign && (
            <div className="note-box" role="status">
              <p>{imageRetryStatus || assets.image_recovery.message || `Image ${assets.image_recovery.failed_image} needs ${assets.image_recovery.retry_scope === 'review' ? 'visual review' : 'correction'}. ${assets.image_recovery.approved_images} approved image(s) are saved. This draft is not approved for delivery.`}</p>
              {(assets.image_recovery.review_reasons || assets.image_recovery.last_review?.review_reasons || []).length > 0 && (
                <ul aria-label="Image review findings">
                  {(assets.image_recovery.review_reasons || assets.image_recovery.last_review?.review_reasons).map((reason, index) => (
                    <li key={`${reason.code}-${index}`}>{reason.detail}</li>
                  ))}
                </ul>
              )}
              {assets.image_recovery.retry_scope === 'review' && <p>The generated image is saved. Review saved image checks those pixels again without starting another FLUX render when its worker checkpoint is available.</p>}
              {campaign.input?.product_image_mode === 'exact_artwork' && (
                <div>
                  <p>Exact artwork needs a transparent product PNG. Upload a replacement, or generate from the description. Generated product artwork may differ from the original label.</p>
                  <label>Upload replacement product artwork
                    <input type="file" accept="image/png" aria-label="Upload replacement product artwork"
                      onChange={uploadRecoveryArtwork} disabled={imageRetryBusy || artworkUploadBusy} />
                  </label>
                  <button type="button" className="secondary-button" onClick={generateRecoveryFromDescription}
                    disabled={imageRetryBusy || artworkUploadBusy}>Generate from description</button>
                </div>
              )}
              {assets.image_recovery.draft_url && <img src={getAssetUrl(assets.image_recovery.draft_url)} alt="Unapproved review draft" style={{ maxWidth: '100%', maxHeight: 320, objectFit: 'contain' }} />}
              <button type="button" className="primary-button" onClick={() => handleImageRetry()}
                disabled={imageRetryBusy || artworkUploadBusy || (campaign.input?.product_image_mode === 'exact_artwork'
                  && !String(campaign.input?.product_image_urls || '').split(',').some((value) => value.trim()))}>
                <RefreshCw size={14} />{imageRetryBusy ? 'Resuming images…' : assets.image_recovery.retry_scope === 'review' ? 'Review saved image' : 'Resume image generation'}
              </button>
            </div>
          )}
          {videoRequested && !videoAvailable && !assets.image_recovery && !generationInProgress && !reviewMode && onUpdateCampaign && (
            <div className="note-box" role="status">
              <p>{videoRetryStatus || (assets.video_recovery
                ? assets.video_recovery.message || `Shot ${assets.video_recovery.failed_shot} of ${assets.video_recovery.shot_count} ${assets.video_recovery.retry_scope === 'review' ? 'needs visual review' : 'needs correction'}. ${assets.video_recovery.approved_shots} approved shot(s) are saved. Your completed images and copy are kept.`
                : 'Retry the video while keeping your completed images and copy. Approved shots are reused when available on this worker.')}</p>
              {(assets.video_recovery?.review_reasons || []).length > 0 && (
                <ul aria-label="Video review findings">{assets.video_recovery.review_reasons.map((reason, index) => (
                  <li key={`${reason.code}-${index}`}>{reason.detail}</li>
                ))}</ul>
              )}
              <button type="button" className="primary-button" onClick={handleVideoRetry} disabled={videoRetryBusy}>
                <RefreshCw size={14} />{videoRetryBusy ? 'Retrying video…' : assets.video_recovery?.retry_scope === 'review'
                  ? 'Review saved shot' : assets.video_recovery ? 'Resume failed shot' : 'Retry video only'}
              </button>
            </div>
          )}
          {imageVariants.length > 0 && (
            <article className="panel output-card glow-card cs-wide" onMouseMove={handleMouseMove}>
              <div className="card-title-row"><ImageIcon size={18} /><h3>Distinct Creative Options</h3></div>
              <p className="cs-muted-line">
                {imageVariants.length > 1 ? 'Distinct creative directions' : 'Creative direction'}, sharing the same product, offer and campaign goal. Copy is rendered in protected negative space; uploaded brand assets are applied from the Brand Hub.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginTop: '16px' }}>
                {imageVariants.map((variant, index) => (
                  <div key={variant.id || variant.image_url || index} style={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '12px', background: 'rgba(255,255,255,0.025)' }}>
                    <div style={{ borderRadius: '12px', overflow: 'hidden', background: 'rgba(255,255,255,0.04)', aspectRatio: variant.width && variant.height ? `${variant.width} / ${variant.height}` : variant.format === 'full_screen' ? '9 / 16' : variant.format === 'portrait' ? '4 / 5' : '1 / 1' }}>
                      <img src={getAssetUrl(variant.image_url)} alt={variant.label || `Creative option ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    </div>
                    <strong style={{ display: 'block', marginTop: '10px' }}>{variant.label || `Creative option ${index + 1}`}</strong>
                    <span style={{ display: 'block', color: 'var(--muted)', fontSize: '0.78rem', marginTop: '4px' }}>{variant.poster_style || variant.format || 'creative direction'}</span>
                    {(variant.delivery_state === 'failed' || variant.review_required) && <p role="status" className="note-box">{variant.delivery_state === 'failed' ? 'Draft: quality checks failed. Review before use.' : 'Quality review required before approval.'}</p>}
                    {(variant.brand_logo_integrated || variant.company_wordmark_integrated || variant.product_reference_integrated) && (
                      <div className="cs-tag-list" style={{ marginTop: '8px' }}>
                        {variant.brand_logo_integrated && (
                          <span>Logo verified {Math.round(Number(variant.logo_integration_score || 0) * 100)}%</span>
                        )}
                        {variant.company_wordmark_integrated && (
                          <span>Company wordmark verified</span>
                        )}
                        {variant.product_reference_integrated && (
                          <span>{identityLabel(variant)}</span>
                        )}
                      </div>
                    )}
                    {variant.design_direction && <p style={{ color: 'var(--muted)', fontSize: '0.78rem', lineHeight: 1.5 }}>{variant.design_direction}</p>}
                    <button
                      onClick={() => handleDownload(
                        variant.image_url_relative || variant.image_url,
                        buildAssetDownloadFilename(
                          campaign,
                          variant.label || variant.id || 'creative',
                          index + 1,
                          'png',
                        ),
                        assetResolver,
                      )}
                      className="secondary-button"
                      style={{ width: '100%', marginTop: '8px', padding: '10px 14px' }}
                    >
                      <Download size={14} /> Download
                    </button>
                  </div>
                ))}
              </div>
            </article>
          )}

          {!reviewMode && <article className="panel output-card glow-card cs-wide" onMouseMove={handleMouseMove}>
            <div className="card-copy-btn">{renderCopyButton(campaign.image_prompt, 'promptCopy')}</div>
            <div className="card-title-row"><Sparkles size={18} /><h3>Creative & Image Direction</h3></div>
            <div className="cs-direction-grid">
              <div className="mini-card"><h4>Design Direction</h4><p>{campaign.design_direction}</p></div>
              <div className="mini-card"><h4>Image Idea</h4><p>{campaign.image_idea}</p></div>
            </div>
            <h4 className="cs-label">Image Generation Prompt</h4>
            <p className="prompt-box">{campaign.image_prompt}</p>
          </article>}
        </div>
      )}

      {/* ===== COPY TAB ===== */}
      {activeTab === 'copy' && (
        <div className="cs-panel reveal-up">
          <div className="cs-grid cs-grid-copy">
            <article className="panel output-card glow-card" onMouseMove={handleMouseMove}>
              <div className="card-copy-btn cs-card-actions">
                {renderRegenButton('ad_headline')}
                {renderCopyButton([campaign.ad_headline, campaign.ad_subheadline, campaign.ad_body, campaign.call_to_action].join('\n'), 'adCopy')}
              </div>
              <div className="card-title-row"><Target size={19} /><h3>Advertising Copy</h3></div>
              <h4 className="cs-headline">{campaign.ad_headline}</h4>
              <p className="cs-subheadline">{campaign.ad_subheadline}</p>
              <p className="cs-body">{campaign.ad_body}</p>
              <div className="cs-overlay-list">
                {(campaign.image_overlay_text || []).map((item, index) => <span key={index}>{item}</span>)}
              </div>
              <div className="cs-cta-pill">{campaign.call_to_action}</div>
            </article>

            <article className="panel output-card glow-card" onMouseMove={handleMouseMove}>
              <div className="card-copy-btn cs-card-actions">
                {renderRegenButton('social_caption')}
                {renderCopyButton(campaign.social_caption, 'socialCopy')}
              </div>
              <div className="card-title-row"><Sparkles size={19} /><h3>Social Content</h3></div>
              <h4 className="cs-headline">{campaign.social_hook}</h4>
              <p className="cs-muted-line">{campaign.social_post_short}</p>
              <p className="cs-body cs-preserve">{campaign.social_caption}</p>
              <div className="cs-tag-list">
                {(campaign.hashtags || []).map((tag) => <span key={tag}>{tag}</span>)}
              </div>
            </article>

            <article className="panel output-card glow-card cs-wide" onMouseMove={handleMouseMove}>
              <div className="card-copy-btn cs-card-actions">
                {renderRegenButton('sales_email_body')}
                {renderCopyButton(campaign.sales_email_body, 'emailCopy')}
              </div>
              <div className="card-title-row"><Mail size={19} /><h3>Sales Email</h3></div>
              <div className="cs-email-subject"><span>Subject</span>{campaign.sales_email_subject}</div>
              <div className="cs-email-body"><p className="cs-preserve">{campaign.sales_email_body}</p></div>
            </article>
          </div>
        </div>
      )}

      {/* ===== STRATEGY TAB ===== */}
      {activeTab === 'strategy' && (
        <div className="cs-panel reveal-up">
          <div className="cs-grid cs-grid-strategy">
            <article className="panel output-card glow-card cs-score-card" onMouseMove={handleMouseMove}>
              <div className="card-title-row"><BarChart3 size={19} /><h3>AI Quality Scores</h3></div>
              <div className="cs-score-meters">
                <p className="cs-muted-line">{scoresAvailable ? 'AI estimates, not measured campaign performance.' : 'Local fallback: independent campaign scoring was not performed.'}</p>
                <ScoreMeter label="Relevance" value={evaluation.relevance} />
                <ScoreMeter label="Creativity" value={evaluation.creativity} />
                <ScoreMeter label="Clarity" value={evaluation.clarity} />
                <ScoreMeter label="Brand fit" value={evaluation.brand_fit} />
                <ScoreMeter label="Conversion" value={evaluation.conversion_potential} />
                <ScoreMeter label="Ethical safety" value={evaluation.ethical_safety} />
              </div>
            </article>

            <article className="panel output-card glow-card" onMouseMove={handleMouseMove}>
              <div className="card-copy-btn">{renderCopyButton(activeNote?.content, 'researchCopy')}</div>
              <div className="card-title-row"><Lightbulb size={19} /><h3>Research Notes</h3></div>
              <div className="research-tabs">
                {researchTabs.map((tab) => (
                  <button key={tab.id} className={`research-tab-btn ${activeResearchTab === tab.id ? 'active' : ''}`} onClick={() => setActiveResearchTab(tab.id)}>
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="cs-research-body">
                <strong>{activeNote?.label}</strong>
                <p>{activeNote?.content}</p>
              </div>
            </article>
          </div>

          {groundingSources.length > 0 && (
            <article className="panel output-card glow-card cs-wide" onMouseMove={handleMouseMove}>
              <div className="card-title-row"><ClipboardList size={19} /><h3>Grounding Sources</h3></div>
              <p className="cs-muted-line">These are the uploaded company knowledge snippets used to ground the campaign.</p>
              <div className="cs-source-list">
                {groundingSources.slice(0, 6).map((source, index) => (
                  <div className="cs-source-card" key={`${source.source || 'source'}-${index}`}>
                    <strong>{source.source || `Source ${index + 1}`}</strong>
                    {typeof source.score === 'number' && <span>Match {(source.score * 100).toFixed(0)}%</span>}
                    <p>{source.snippet}</p>
                  </div>
                ))}
              </div>
            </article>
          )}

          <article className="panel output-card glow-card cs-wide" onMouseMove={handleMouseMove}>
            <div className="card-title-row"><CheckCircle2 size={19} /><h3>Improvement Tips</h3></div>
            <div className="cs-tips-grid">
              {(campaign.improvement_tips || []).map((tip, index) => (
                <div key={index} className="cs-tip">{tip}</div>
              ))}
            </div>
          </article>
        </div>
      )}

      {/* ===== PLANNING TAB ===== */}
      {activeTab === 'planning' && (
        <div className="cs-panel reveal-up">
          <div className="cs-grid cs-grid-copy">
            {videoEnabled && (
              <article className="panel output-card glow-card" onMouseMove={handleMouseMove}>
                <div className="card-copy-btn">{renderCopyButton((campaign.video_storyboard || []).map((scene, index) => formatStoryboardScene(scene, index, 24)).join('\n\n'), 'storyboardCopy')}</div>
                <div className="card-title-row"><Video size={19} /><h3>Video Storyboard</h3></div>
                <ol className="cs-storyboard">
                  {(campaign.video_storyboard || []).map((scene, index) => <li key={index} style={{ whiteSpace: 'pre-wrap' }}>{formatStoryboardScene(scene, index, 24)}</li>)}
                </ol>
              </article>
            )}

            <article className="panel output-card glow-card" onMouseMove={handleMouseMove}>
              <div className="card-copy-btn">{renderCopyButton((campaign.content_calendar || []).join('\n'), 'calendarCopy')}</div>
              <div className="card-title-row"><CalendarDays size={19} /><h3>Content Calendar</h3></div>
              <ul className="cs-calendar">
                {(campaign.content_calendar || []).map((item, index) => <li key={index}>{item}</li>)}
              </ul>
            </article>
          </div>
        </div>
      )}
    </section>
  );
}

export default ResultDashboard;
